import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button,
  CircularProgress, Alert, LinearProgress
} from '@mui/material';
import {
  Assignment as RecipeIcon,
  PlaylistAddCheck as OrderIcon,
  AttachMoney as CostIcon,
  TrendingUp as YieldIcon,
  Add as AddIcon,
  Inventory as GrainIcon,
  Storage as SiloIcon,
  Blender as BlendIcon,
  WaterDrop as TemperIcon,
  Science as QualityIcon,
  BugReport as AflatoxinIcon,
  MedicalServices as FortIcon,
  Settings as DegermIcon,
  CalendarMonth as ScheduleIcon,
  BarChart as AnalyticsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi, { MillingRecipe, MillingOrder, MillingCostConfig } from '../../services/millingApi';

interface DashboardStats {
  totalRecipes: number;
  activeRecipes: number;
  totalOrders: number;
  ordersByStatus: Record<string, number>;
  totalCostConfigs: number;
  recentOrders: MillingOrder[];
}

const FlourMilling: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DashboardStats>({
    totalRecipes: 0,
    activeRecipes: 0,
    totalOrders: 0,
    ordersByStatus: {},
    totalCostConfigs: 0,
    recentOrders: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel
      const [recipesRes, ordersRes, costsRes] = await Promise.all([
        millingApi.getRecipes({}).catch(() => ({ data: [], total: 0 })),
        millingApi.getOrders({ limit: 10 }).catch(() => ({ data: [], total: 0 })),
        millingApi.getCostConfigs({}).catch(() => ({ data: [], total: 0 })),
      ]);

      const recipes = recipesRes.data || recipesRes || [];
      const orders = ordersRes.data || ordersRes || [];
      const costs = costsRes.data || costsRes || [];

      // Calculate stats
      const ordersByStatus: Record<string, number> = {};
      orders.forEach((o: MillingOrder) => {
        ordersByStatus[o.status] = (ordersByStatus[o.status] || 0) + 1;
      });

      setStats({
        totalRecipes: recipes.length,
        activeRecipes: recipes.filter((r: MillingRecipe) => r.status === 'active').length,
        totalOrders: ordersRes.total || orders.length,
        ordersByStatus,
        totalCostConfigs: costs.length,
        recentOrders: orders.slice(0, 5),
      });
    } catch (err) {
      console.error(err);
      setError('Error loading dashboard data. Ensure the milling backend service is running on port 3006.');
    } finally {
      setLoading(false);
    }
  };

  const statusColors: Record<string, string> = {
    draft: '#9e9e9e',
    pending_approval: '#ff9800',
    approved: '#2196f3',
    in_progress: '#3f51b5',
    completed: '#4caf50',
    cancelled: '#f44336',
    on_hold: '#ff9800',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Flour Milling Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/milling/recipes/new')}>
            New Recipe
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/orders/new')}>
            New Order
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RecipeIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.totalRecipes}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Recipes</Typography>
                </Box>
              </Box>
              <Chip
                label={`${stats.activeRecipes} Active`}
                color="success"
                size="small"
              />
              <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/milling/recipes')}>
                View All
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OrderIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.totalOrders}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {Object.entries(stats.ordersByStatus).slice(0, 3).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${count} ${status.replace('_', ' ')}`}
                    size="small"
                    sx={{ bgcolor: statusColors[status], color: 'white' }}
                  />
                ))}
              </Box>
              <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/milling/orders')}>
                View All
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CostIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">{stats.totalCostConfigs}</Typography>
                  <Typography variant="body2" color="text.secondary">Cost Configs</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Labor, overhead, depreciation
              </Typography>
              <Button size="small" onClick={() => navigate('/milling/costs')}>
                Manage Costs
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <YieldIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h4">-</Typography>
                  <Typography variant="body2" color="text.secondary">Avg Yield %</Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Track milling efficiency
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Orders */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Milling Orders</Typography>
              {stats.recentOrders.length > 0 ? (
                <Box>
                  {stats.recentOrders.map((order) => (
                    <Box
                      key={order.id}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 1.5,
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#f5f5f5' },
                      }}
                      onClick={() => navigate(`/milling/orders/${order.id}`)}
                    >
                      <Box>
                        <Typography fontWeight="bold">{order.orderNumber}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.recipeName} | {new Date(order.orderDate).toLocaleDateString()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip
                          label={order.status.replace('_', ' ')}
                          size="small"
                          sx={{ bgcolor: statusColors[order.status], color: 'white', mb: 0.5 }}
                        />
                        <Typography variant="body2">
                          {order.plannedOutputQty} units planned
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No orders yet. Create your first milling order!
                </Typography>
              )}
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button onClick={() => navigate('/milling/orders')}>View All Orders</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<RecipeIcon />}
                  onClick={() => navigate('/milling/recipes')}
                >
                  Manage Recipes
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<OrderIcon />}
                  onClick={() => navigate('/milling/orders')}
                >
                  View Orders
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CostIcon />}
                  onClick={() => navigate('/milling/costs')}
                >
                  Cost Configurations
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Raw Material Management</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<GrainIcon />}
                  onClick={() => navigate('/milling/grain-lots')}
                >
                  Grain Lots
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SiloIcon />}
                  onClick={() => navigate('/milling/silos')}
                >
                  Silo Management
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<BlendIcon />}
                  onClick={() => navigate('/milling/grist-recipes')}
                >
                  Grist / Blend Recipes
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<TemperIcon />}
                  onClick={() => navigate('/milling/tempering')}
                >
                  Tempering Records
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<QualityIcon />}
                  onClick={() => navigate('/milling/quality-tests')}
                >
                  Quality Tests & CoA
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AflatoxinIcon />}
                  onClick={() => navigate('/milling/aflatoxin')}
                >
                  Aflatoxin Monitoring
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<FortIcon />}
                  onClick={() => navigate('/milling/fortification')}
                >
                  Fortification Records
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DegermIcon />}
                  onClick={() => navigate('/milling/degerming')}
                >
                  Maize Degerming
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ScheduleIcon />}
                  onClick={() => navigate('/milling/schedule')}
                >
                  Milling Schedule
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<AnalyticsIcon />}
                  onClick={() => navigate('/milling/analytics')}
                  color="secondary"
                >
                  Analytics Dashboard
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Milling Process</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Standard milling workflow:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['1. Create Recipe', '2. Create Order', '3. Issue Materials', '4. Process', '5. Receive Outputs', '6. Calculate Costs'].map((step, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip label={idx + 1} size="small" sx={{ mr: 1, minWidth: 24 }} />
                    <Typography variant="body2">{step.substring(3)}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FlourMilling;
